import React, { useEffect, useState } from "react";
import { AgCharts } from "ag-charts-react";
import { AgChartOptions } from "ag-charts-community";

export interface ChartData {
  [key: string | number]: string | number;
}

interface ChartProps {
  data: ChartData[];
  type: "line" | "bar" | "scatter" | "area" ;
  xAxisKey: string;
  title?: string;
  xLabel?: string;
  yLabel?: string;
  showLegend?: boolean;
  legendNames?: { [key: string]: string };
  legendPosition?: "top" | "bottom" | "right" | "left";
  showTooltip?: boolean;
  secondaryAxisKeys?: string[];
  secondaryAxisLabel?: string;
}

export const Chart: React.FC<ChartProps> = ({
  data = [],
  type = "line",
  xAxisKey,
  title,
  xLabel,
  yLabel,
  showLegend = false,
  legendNames,
  legendPosition = "bottom",
  showTooltip = true,
  secondaryAxisKeys,
  secondaryAxisLabel,
}) => {
  const [loading, setLoading] = useState(false);
  const [series, setSeries] = useState<any>([]);
  const [primaryKeys, setPrimaryKeys] = useState<string[]>([]);
  const typeOfKey = typeof data[0]?.[xAxisKey];
  useEffect(() => {
    // console.log(title, " : ", data)
    if (!data.length) {
      return;
    }
    setLoading(true);
    const element = data[0];
    let keys = Object.keys(element);
    keys = keys.filter((key) => key !== xAxisKey);

    // if secondary axis in props than filter out secondary axis from primaryKeys
    if (secondaryAxisKeys && secondaryAxisKeys.length > 0) {
      const updatedKeys = keys.filter(
        (key) => !secondaryAxisKeys?.includes(key)
      );
      setPrimaryKeys(updatedKeys);
    }

    const areaChartOptions = {
      stroke: "#159fff",
      strokeWidth: 3,
      fill: "lightBlue",
    }
    const seriesObject: any = [];
    keys.forEach((key) => {
      const seriesItem = {
        type: type,
        xKey: xAxisKey,
        yKey: key,
        yName: (legendNames && legendNames[key]) || key,
        marker: {
          enabled: true,
        },
      };
      const areaChartObject = {...seriesItem, ...areaChartOptions}
      if(type === "area"){
        seriesObject.push(areaChartObject)
      } else {
        seriesObject.push(seriesItem);
      }
    });

    setSeries(seriesObject);
    setLoading(false);
  }, [data]);

  const defaultOptions: AgChartOptions = {
    title: {
      text: title || "",
      fontSize: 13,
      // fontWeight: 'bold',
    },
    data: data,
    series: series,
    axes: [
      {
        type: typeOfKey === "object" ? "ordinal-time" : "category",
        position: "bottom",
        label: {
          avoidCollisions: true,
          // rotation: 335
        },
        title: {
          fontSize: 12,
          text: xLabel || "",
        },
      },
      // primary axis
      {
        type: "number",
        position: "left",
        keys: primaryKeys,
        label: {
          avoidCollisions: true,
        },
        title: {
          fontSize: 12,
          text: yLabel || "",
        },
      },

      // secondary y-axis
      {
        type: "number",
        position: "right",
        keys: secondaryAxisKeys,
        label: {
          avoidCollisions: true,
        },
        title: {
          fontSize: 12,
          text: secondaryAxisLabel || "",
        },
      },
    ],
    legend: {
      enabled: showLegend,
      position: legendPosition,
    },
    tooltip: {
      enabled: showTooltip,
    },
  };

  return (
    <div>
      {loading ? (
        <div className="text-center py-5 h-100">Loading...</div>
      ) : data.length ? (
        <AgCharts options={defaultOptions} style={{ height: 350 }} />
      ) : ''}
    </div>
  );
};
